import { ReactNode } from 'react';
import { Trans } from '@lingui/react/macro';

import { ActivityOfType } from '@/components/activities/Activity';
import { BaseActivity, BaseActivityText } from '@/components/activities/BaseActivity/BaseActivity';
import IconEdit from '@/icons/Edit.svg';

export const RequestPropertyUpdatedActivity = ({
  activity,
}: {
  activity: ActivityOfType<'request_property_updated'>;
}) => {
  const subComponentProps = {
    isClientCreator: !!activity.clientCreator,
    isStoreCreator: !!activity.storeCreator,
    creatorName: activity.creator?.name,
  };

  let requestPropertyUpdatedMessage: ReactNode | undefined;

  if (activity.data.property === 'priority') {
    requestPropertyUpdatedMessage = <RequestPriorityUpdatedTranslation {...subComponentProps} />;
  } else if (activity.data.property === 'supervisor' && activity.data.after === null) {
    requestPropertyUpdatedMessage = <RequestSupervisorRemovedTranslation {...subComponentProps} />;
  } else if (activity.data.property === 'supervisor') {
    requestPropertyUpdatedMessage = <RequestSupervisorAddedTranslation {...subComponentProps} />;
  } else if (activity.data.property === 'client_info') {
    requestPropertyUpdatedMessage = <RequestClientInfoUpdatedTranslation {...subComponentProps} />;
  }
  if (!requestPropertyUpdatedMessage) return null;

  return (
    <BaseActivity
      key={activity.id}
      createdAt={activity.createdAt}
      icon={<IconEdit />}
      message={<BaseActivityText>{requestPropertyUpdatedMessage}</BaseActivityText>}
    />
  );
};

const RequestPriorityUpdatedTranslation = ({
  creatorName,
  isStoreCreator,
}: {
  creatorName?: string;
  isClientCreator: boolean;
  isStoreCreator: boolean;
}) => {
  if (isStoreCreator) {
    return (
      <Trans id="activities.request_property_updated.priority.store">
        <strong>The store</strong> edited the priority
      </Trans>
    );
  }

  return (
    <Trans id="activities.request_property_updated.priority.creator">
      <strong>{creatorName}</strong> edited the priority
    </Trans>
  );
};

const RequestSupervisorAddedTranslation = ({
  creatorName,
  isStoreCreator,
}: {
  creatorName?: string;
  isStoreCreator: boolean;
}) => {
  if (isStoreCreator) {
    return (
      <Trans id="activities.request_property_updated.supervisor.new.store">
        <strong>The store</strong> assigned a new supervisor to the request
      </Trans>
    );
  }

  return (
    <Trans id="activities.request_property_updated.supervisor.new.creator">
      <strong>{creatorName}</strong> assigned a new supervisor to the request
    </Trans>
  );
};

const RequestSupervisorRemovedTranslation = ({
  creatorName,
  isStoreCreator,
}: {
  creatorName?: string;
  isStoreCreator: boolean;
}) => {
  if (isStoreCreator) {
    return (
      <Trans id="activities.request_property_updated.supervisor.null.store">
        <strong>The store</strong> removed the supervisor of the request
      </Trans>
    );
  }

  return (
    <Trans id="activities.request_property_updated.supervisor.null.creator">
      <strong>{creatorName}</strong> removed the supervisor of the request
    </Trans>
  );
};

const RequestClientInfoUpdatedTranslation = ({
  creatorName,
  isClientCreator,
  isStoreCreator,
}: {
  creatorName?: string;
  isClientCreator: boolean;
  isStoreCreator: boolean;
}) => {
  if (isClientCreator) {
    return (
      <Trans id="activities.request_property_updated.client_info.client">
        <strong>The client</strong> edited the client info
      </Trans>
    );
  }

  if (isStoreCreator) {
    return (
      <Trans id="activities.request_property_updated.client_info.store">
        <strong>The store</strong> edited the client info
      </Trans>
    );
  }

  return (
    <Trans id="activities.request_property_updated.client_info.creator">
      <strong>{creatorName}</strong> edited the client info
    </Trans>
  );
};
